import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import 'moment/locale/he'

import { Layout, DatePicker, Empty, Spin, message, Table, Select, Button, Modal, Popconfirm, Tooltip } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import mentors from '@services/mentors'
import MentorCard from './MentorCard'
import MeetingModal from './MeetingModal'
import {
    renderStatusCell,
    isMeetingPast,
    isBeforeMeetingsStart,
    isBeforeMeetingsStartMonth,
    FutureMeetingTag
} from '@utils/meetingStatus'
import { downloadBlobFile } from '@utils/index'
import * as s from './style.scss'

const { Option } = Select

const mentorColors = [
    '#1890ff',
    '#52c41a',
    '#eb2f96',
    '#722ed1',
    '#13c2c2',
    '#fa541c',
    '#d4a017',
    '#f5222d',
    '#2f54eb',
    '#a0d911'
]

const TABS = [
    { key: 'coordinators', label: 'מתאמות פגישות' },
    { key: 'mentors', label: 'מנטורים' }
]

interface Meeting {
    id: number
    meeting_time: string
    status: string
    created_by?: number
    mentor?: { id: number; name?: string }
    agent?: { id: number; first_name?: string; last_name?: string; phone?: string }
    contact_name?: string
    contact_phone?: string
    contact_agent_name?: string
    creator?: { id: number; first_name?: string; last_name?: string }
}

interface MentorOrCoordinator {
    id: number
    name: string
    monthCreatedCount?: number
}

const COORDINATOR_GOAL = 300

interface MentorsMeetingsState {
    meetings: Meeting[]
    mentorsList: MentorOrCoordinator[]
    coordinatorsList: MentorOrCoordinator[]
    month: moment.Moment
    day: moment.Moment
    viewMode: 'daily' | 'monthly'
    loading: boolean
    activeTab: 'mentors' | 'coordinators'
    undocumentedMeetings: Meeting[]
    undocumentedLoading: boolean
    undocumentedCreatedBy?: number
    editingMeetingId: number | null
    createModalVisible: boolean
    recentMeetingsVisible: boolean
    recentMeetings: Meeting[]
    recentMeetingsLoading: boolean
    exportLoading: boolean
    exportRecentLoading: boolean
    exportModalVisible: boolean
    exportMonth: moment.Moment
    exportRecentModalVisible: boolean
    exportRecentMonth: moment.Moment
    lastRefreshedAt: moment.Moment | null
}

// the dashboard is left open all day, so it re-pulls the stats on its own
const AUTO_REFRESH_MS = 20 * 60 * 1000

@inject((store: any) => {
    const { authStore } = store
    return { authStore }
})
@observer
class MentorsMeetings extends Component<any, MentorsMeetingsState> {
    refreshTimer: any

    constructor(props) {
        super(props)
        this.refreshTimer = null
        this.state = {
            meetings: [],
            mentorsList: [],
            coordinatorsList: [],
            month: moment(),
            day: moment(),
            viewMode: 'daily',
            loading: false,
            activeTab: 'coordinators',
            undocumentedMeetings: [],
            undocumentedLoading: false,
            undocumentedCreatedBy: undefined,
            editingMeetingId: null,
            createModalVisible: false,
            recentMeetingsVisible: false,
            recentMeetings: [],
            recentMeetingsLoading: false,
            exportLoading: false,
            exportRecentLoading: false,
            exportModalVisible: false,
            exportRecentModalVisible: false,
            exportRecentMonth: moment(),
            exportMonth: moment(),
            lastRefreshedAt: null
        }
    }

    componentDidMount() {
        // coordinatorsList (needed to know if the current user is a coordinator) only arrives from loadMeetings
        this.loadMeetings().then(this.loadUndocumentedMeetings)
        this.refreshTimer = setInterval(this.refreshAll, AUTO_REFRESH_MS)
    }

    componentWillUnmount() {
        clearInterval(this.refreshTimer)
    }

    // manual refresh button and the auto-refresh timer share this.
    // coordinatorsList is already loaded by now, so both requests can go out together
    refreshAll = () => {
        this.loadMeetings()
        this.loadUndocumentedMeetings()
    }

    loadUndocumentedMeetings = () => {
        const { undocumentedCreatedBy } = this.state
        this.setState({ undocumentedLoading: true })
        mentors
            .getUndocumentedMeetings(undocumentedCreatedBy ? { created_by: undocumentedCreatedBy } : {})
            .then(res => this.setState({ undocumentedMeetings: res.data || [], undocumentedLoading: false }))
            .catch(() => {
                this.setState({ undocumentedLoading: false })
                message.error('שגיאה בטעינת פגישות שלא תועדו')
            })
    }

    // last 90 days of meetings entered into the system (by created_at) — a coordinator sees
    // only their own, anyone else (e.g. admin) sees every coordinator's
    openRecentMeetings = () => {
        const { id: currentUserId } = this.props.authStore
        const { coordinatorsList } = this.state
        const isCurrentUserCoordinator = coordinatorsList.some(c => c.id === currentUserId)

        this.setState({ recentMeetingsVisible: true, recentMeetingsLoading: true })
        mentors
            .getRecentMeetings(isCurrentUserCoordinator ? { created_by: currentUserId } : {})
            .then(res => this.setState({ recentMeetings: res.data || [], recentMeetingsLoading: false }))
            .catch(() => {
                this.setState({ recentMeetingsLoading: false })
                message.error('שגיאה בטעינת פגישות אחרונות שתואמו')
            })
    }

    handleDeleteMeeting = (meeting: Meeting) => {
        mentors
            .deleteMeeting(meeting.id)
            .then(() => {
                this.openRecentMeetings()
                this.loadMeetings()
            })
            .catch(() => message.error('שגיאה במחיקת פגישה'))
    }

    handleUndocumentedStatusChange = (meeting: Meeting, status: string) => {
        this.setState({ editingMeetingId: null })
        mentors
            .updateMeeting(meeting.id, { status })
            .then(() => {
                message.success('הפגישה עודכנה')
                this.loadUndocumentedMeetings()
                this.loadMeetings()
            })
            .catch(() => {
                message.error('שגיאה בעדכון פגישה')
            })
    }

    loadMeetings = () => {
        const { month, day, viewMode, activeTab } = this.state
        this.setState({ loading: true })
        const payload =
            this.effectiveViewMode(activeTab, viewMode) === 'daily'
                ? { month: day.month() + 1, year: day.year(), day: day.format('YYYY-MM-DD') }
                : { month: month.month() + 1, year: month.year() }
        return mentors
            .getMeetingsDashboard(payload)
            .then(res =>
                this.setState({
                    meetings: res.data.meetings || [],
                    mentorsList: res.data.mentors || [],
                    coordinatorsList: res.data.coordinators || [],
                    loading: false,
                    lastRefreshedAt: moment()
                })
            )
            .catch(() => {
                this.setState({ loading: false })
                message.error('שגיאה בטעינת הפגישות')
            })
    }

    // exports the whole month picked above (defaults to the current one); the server builds
    // the excel and decides which meetings the logged-in user is allowed to see
    handleExport = () => {
        const { exportMonth } = this.state
        this.setState({ exportLoading: true })
        mentors
            .exportMeetings({ month: exportMonth.month() + 1, year: exportMonth.year() })
            .then(res => downloadBlobFile(res, `פגישות-${exportMonth.format('MM-YYYY')}.xlsx`))
            .catch(() => message.error('שגיאה בייצוא הפגישות'))
            .finally(() => this.setState({ exportLoading: false, exportModalVisible: false }))
    }

    // exports the whole month picked above (defaults to the current one); the server builds
    // the excel and decides which meetings the logged-in user is allowed to see
    handleExportRecent = () => {
        const { exportRecentMonth } = this.state
        this.setState({ exportRecentLoading: true })
        mentors
            .exportRecentMeetings({ month: exportRecentMonth.month() + 1, year: exportRecentMonth.year() })
            .then(res => downloadBlobFile(res, `פגישות-אחרונות-${exportRecentMonth.format('MM-YYYY')}.xlsx`))
            .catch(() => message.error('שגיאה בייצוא הפגישות'))
            .finally(() => this.setState({ exportRecentLoading: false, exportRecentModalVisible: false }))
    }

    handleMonthChange = date => {
        this.setState({ month: date || moment() }, this.loadMeetings)
    }

    handleDayChange = (date: moment.Moment) => {
        this.setState({ day: date || moment() }, this.loadMeetings)
    }

    // the coordinators tab has no daily view — only the mentors tab can switch modes, so the
    // stored viewMode is ignored there and every consumer reads monthly
    effectiveViewMode = (activeTab: 'mentors' | 'coordinators', viewMode: 'daily' | 'monthly') =>
        activeTab === 'mentors' ? viewMode : 'monthly'

    handleViewModeChange = (mode: 'daily' | 'monthly') => {
        this.setState({ viewMode: mode }, this.loadMeetings)
    }

    getColorForGroup = (mentorId: number, list: MentorOrCoordinator[]) => {
        const index = list.findIndex(m => m.id === mentorId)
        return mentorColors[index % mentorColors.length]
    }

    render() {
        const {
            meetings,
            mentorsList,
            coordinatorsList,
            month,
            day,
            viewMode,
            loading,
            activeTab,
            undocumentedMeetings,
            undocumentedLoading,
            undocumentedCreatedBy,
            editingMeetingId,
            createModalVisible,
            recentMeetingsVisible,
            recentMeetings,
            recentMeetingsLoading,
            exportLoading,
            exportRecentLoading,
            exportModalVisible,
            exportMonth,
            exportRecentModalVisible,
            exportRecentMonth,
            lastRefreshedAt
        } = this.state
        const { id: currentUserId } = this.props.authStore
        const isCurrentUserCoordinator = coordinatorsList.some(c => c.id === currentUserId)
        // the buckets split 'not_documented' on "has this date passed", which only reads right for
        // the month being viewed. Against today, a past month puts every row in "לא תועדו" and a
        // future month puts every row in "נותרו", so the ring barely changes as you page months.
        // Clamping the reference to the selected month's end fixes both; the current month and the
        // daily view keep comparing against today
        const monthEnd = month.clone().endOf('month')
        const currentViewMode = this.effectiveViewMode(activeTab, viewMode)
        const now = currentViewMode === 'monthly' && monthEnd.isBefore(moment()) ? monthEnd : moment()
        const activeList = activeTab === 'coordinators' ? coordinatorsList : mentorsList
        const coordinatorIds = coordinatorsList.map(c => c.id)

        // every meeting starts as 'not_documented', so a past row still in that state means the mentor never reported — a real bucket, not missing data
        const isPast = (x: Meeting) => isMeetingPast(x.meeting_time, now)

        // "rest" is meetings not yet documented whose date hasn't passed
        const isRest = (x: Meeting) => x.status === 'not_documented' && !isPast(x)

        // "not documented" is the same status but the date already passed
        const isNotDocumented = (x: Meeting) => x.status === 'not_documented' && isPast(x)

        // created_by NULL (CRM-synced rows) counts as a mentor meeting, not a coordinator one
        const isCoordinatorRow = (x: Meeting) => Boolean(x.created_by) && coordinatorIds.includes(x.created_by)

        // mentors tab shows every meeting regardless of who scheduled it; coordinators tab filters down to coordinator-created rows
        const tabMeetings = activeTab === 'coordinators' ? meetings.filter(isCoordinatorRow) : meetings

        // one place that turns a list of meetings into the four buckets
        const statsFor = (id: number, name: string, rows: Meeting[]) => {
            const occurred = rows.filter(x => x.status === 'occurred')
            return {
                id,
                name,
                total: rows.length,
                occurred,
                notOccurred: rows.filter(x => x.status === 'not_occurred'),
                notDocumented: rows.filter(isNotDocumented),
                rest: rows.filter(isRest),
                occurredPercent: rows.length ? Math.round((occurred.length / rows.length) * 100) : 0
            }
        }

        // on the mentors tab, one box per mentor; on the coordinators tab, one box per coordinator (grouped by who scheduled the meeting)
        const rowsForGroup = (g: MentorOrCoordinator) =>
            activeTab === 'coordinators'
                ? tabMeetings.filter(x => x.created_by === g.id)
                : tabMeetings.filter(x => x.mentor && x.mentor.id === g.id)

        const perMentor =
            activeTab === 'coordinators'
                ? activeList
                      // coordinators tab: ring counts the meetings this coordinator created during the month
                      // (by creation date, any status) toward the fixed goal
                      .map(g => ({
                          id: g.id,
                          name: g.name,
                          goal: COORDINATOR_GOAL,
                          createdCount: g.monthCreatedCount || 0
                      }))
                      // coordinators with no meetings this month still get a card showing 0,
                      // rather than dropping out and leaving an empty state
                      .sort((a, b) => b.createdCount - a.createdCount)
                : activeList.map(g => statsFor(g.id, g.name, rowsForGroup(g))).sort((a, b) => b.total - a.total)

        const allStats = statsFor(-1, 'כל המנטורים', tabMeetings)

        const undocumentedColumns: ColumnProps<Meeting>[] = [
            {
                title: 'תאריך',
                dataIndex: 'meeting_time',
                width: 215,
                align: 'right',
                render: value => (
                    <span>
                        {moment(value).format('DD/MM/YYYY HH:mm')}
                        {moment(value).isAfter(moment()) ? <FutureMeetingTag /> : null}
                    </span>
                )
            },
            {
                title: 'סוכן',
                dataIndex: 'agent',
                width: 160,
                align: 'right',
                render: (agent, record: Meeting) =>
                    agent ? `${agent.first_name || ''} ${agent.last_name || ''}`.trim() : record.contact_name || '—'
            },
            {
                title: 'מנטור',
                dataIndex: 'mentor',
                width: 140,
                align: 'right',
                render: mentor => (mentor ? mentor.name : '—')
            },
            {
                title: 'נוצרה על ידי',
                dataIndex: 'creator',
                width: 140,
                align: 'right',
                render: creator => (creator ? `${creator.first_name || ''} ${creator.last_name || ''}`.trim() : '—')
            },
            {
                title: 'סטטוס',
                dataIndex: 'status',
                width: 160,
                align: 'center',
                render: (status, record: Meeting) =>
                    renderStatusCell({
                        status,
                        recordId: record.id,
                        editingMeetingId,
                        onEdit: id => this.setState({ editingMeetingId: id }),
                        onCancelEdit: () => this.setState({ editingMeetingId: null }),
                        onChange: value => this.handleUndocumentedStatusChange(record, value)
                    })
            }
        ]

        // same as undocumentedColumns, plus a delete action — only used by the recent-meetings
        // modal, so the "לא תועדו" table stays untouched
        const recentMeetingsColumns: ColumnProps<Meeting>[] = [
            ...undocumentedColumns,
            {
                title: '',
                dataIndex: 'actions',
                width: 50,
                align: 'center',
                render: (_, record: Meeting) => (
                    <Popconfirm
                        title="האם למחוק את הפגישה?"
                        okText="מחיקה"
                        cancelText="ביטול"
                        okType="danger"
                        overlayClassName="mentor-meetings-delete-popconfirm"
                        onConfirm={() => this.handleDeleteMeeting(record)}
                    >
                        <Tooltip title="מחיקת פגישה">
                            <Button
                                shape="circle"
                                size="small"
                                icon="delete"
                                style={{ color: '#ff4d4f', borderColor: 'transparent', background: 'transparent' }}
                            />
                        </Tooltip>
                    </Popconfirm>
                )
            }
        ]

        return (
            <Layout className={s.page}>
                <div className={s.toolbar}>
                    <div className={s.titleBlock}>
                        <h1 className={s.pageTitle}>{'פגישות מנטורים'}</h1>
                        <span className={s.pageSubTitle}>
                            {activeTab === 'mentors' && viewMode === 'daily'
                                ? day.format('DD/MM/YYYY')
                                : month.format('MMMM YYYY')}
                        </span>
                    </div>

                    {/* segmented pill control — fixed height so it lines up with the picker */}
                    <div className={s.segmented}>
                        {TABS.map(t => (
                            <button
                                key={t.key}
                                type="button"
                                className={`${s.segmentedItem} ${activeTab === t.key ? s.segmentedItemActive : ''}`}
                                onClick={() => this.setState({ activeTab: t.key }, this.loadMeetings)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'mentors' && viewMode === 'daily' ? (
                        <div className={s.monthPickerWrap}>
                            <span className={s.monthPickerLabel}>{'בחר יום:'}</span>
                            <DatePicker
                                value={day}
                                onChange={this.handleDayChange}
                                format="DD/MM/YYYY"
                                allowClear={false}
                                disabledDate={isBeforeMeetingsStart}
                                style={{ width: '150px' }}
                            />
                        </div>
                    ) : (
                        <div className={s.monthPickerWrap}>
                            <span className={s.monthPickerLabel}>{'בחר חודש:'}</span>
                            <DatePicker.MonthPicker
                                value={month}
                                onChange={this.handleMonthChange}
                                format="MM/YYYY"
                                allowClear={false}
                                disabledDate={isBeforeMeetingsStartMonth}
                                style={{ width: '150px' }}
                            />
                        </div>
                    )}
                </div>

                {/* own row under the heading, so the title/tabs/picker row keeps its layout.
                    outside the tab check, so both tabs can refresh. viewMode switch shares this row when on the mentors tab */}
                <div className={s.refreshRow}>
                    {activeTab === 'mentors' && (
                        <Button
                            icon="file-excel"
                            disabled={loading}
                            onClick={() => this.setState({ exportModalVisible: true, exportMonth: month })}
                            className={s.exportBtn}
                        >
                            {'ייצוא לאקסל'}
                        </Button>
                    )}
                    {activeTab === 'coordinators' && (
                        <Button
                            icon="file-excel"
                            disabled={loading}
                            onClick={() => this.setState({ exportRecentModalVisible: true, exportRecentMonth: month })}
                            className={s.exportBtn}
                        >
                            {'ייצוא לאקסל'}
                        </Button>
                    )}
                    {activeTab === 'mentors' && (
                        <div className={s.viewModeSwitch}>
                            <button
                                type="button"
                                className={`${s.viewModeItem} ${viewMode === 'daily' ? s.viewModeItemActive : ''}`}
                                onClick={() => this.handleViewModeChange('daily')}
                            >
                                {'יומי'}
                            </button>
                            <span className={s.viewModeSep}>{'/'}</span>
                            <button
                                type="button"
                                className={`${s.viewModeItem} ${viewMode === 'monthly' ? s.viewModeItemActive : ''}`}
                                onClick={() => this.handleViewModeChange('monthly')}
                            >
                                {'חודשי'}
                            </button>
                        </div>
                    )}
                    <div className={s.refreshGroup}>
                        <Button icon="reload" loading={loading} onClick={this.refreshAll}>
                            {'רענון'}
                        </Button>
                        {lastRefreshedAt && (
                            <span className={s.refreshStamp}>{`עודכן ב־${lastRefreshedAt.format('HH:mm')}`}</span>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <Spin size="large" />
                    </div>
                ) : perMentor.length > 0 ? (
                    <div
                        className={
                            activeTab === 'coordinators' ? `${s.mentorGrid} ${s.mentorGridCentered}` : s.mentorGrid
                        }
                    >
                        {perMentor.map(m => {
                            const color = this.getColorForGroup(m.id, activeList)
                            return <MentorCard key={m.id} mentor={m} color={color} viewMode={currentViewMode} />
                        })}

                        {activeTab === 'mentors' && (
                            <div className={s.allMentorsGrid}>
                                <MentorCard mentor={allStats} color="#e77910" viewMode={currentViewMode} />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={`${s.card} ${s.panel}`} style={{ marginTop: '16px', marginBottom: '16px' }}>
                        <Empty
                            description={
                                currentViewMode === 'daily' ? 'אין פגישות היום' : 'אין פגישות החודש'
                            }
                        />
                    </div>
                )}

                {activeTab === 'coordinators' && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '45px' }}>
                        <Button icon="unordered-list" onClick={this.openRecentMeetings}>
                            {'פגישות אחרונות שתואמו'}
                        </Button>
                        <Button type="primary" icon="plus" onClick={() => this.setState({ createModalVisible: true })}>
                            {'פגישה עם סוכן חדש'}
                        </Button>
                    </div>
                )}

                {activeTab === 'coordinators' && (
                    <div className={`${s.card} ${s.undocumentedCard}`}>
                        <div className={s.undocumentedTitle}>
                            <span>{'פגישות שלא תועדו'}</span>
                            {!isCurrentUserCoordinator && (
                                <Select
                                    allowClear
                                    placeholder="סינון לפי יוצר הפגישה"
                                    className={s.undocumentedFilter}
                                    value={undocumentedCreatedBy}
                                    onChange={(value: number) =>
                                        this.setState({ undocumentedCreatedBy: value }, this.loadUndocumentedMeetings)
                                    }
                                >
                                    {coordinatorsList.map(c => (
                                        <Option key={c.id} value={c.id}>
                                            {c.name}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        </div>

                        <Table
                            className={`${s.undocumentedTable} ${s.tableGradient}`}
                            rowKey="id"
                            dataSource={undocumentedMeetings}
                            columns={undocumentedColumns}
                            pagination={{ pageSize: 10 }}
                            loading={undocumentedLoading}
                            size="middle"
                            locale={{ emptyText: 'אין פגישות שלא תועדו' }}
                        />
                    </div>
                )}

                {activeTab === 'coordinators' && (
                    <MeetingModal
                        mode="search"
                        visible={createModalVisible}
                        authStore={this.props.authStore}
                        coordinatorsList={coordinatorsList}
                        onClose={() => this.setState({ createModalVisible: false })}
                        onCreated={() => {
                            this.loadMeetings()
                            this.loadUndocumentedMeetings()
                        }}
                    />
                )}
                {activeTab === 'coordinators' && (
                    <Modal
                        title="פגישות אחרונות שתואמו"
                        className="mentor-meetings-modal"
                        visible={recentMeetingsVisible}
                        onCancel={() => this.setState({ recentMeetingsVisible: false, editingMeetingId: null })}
                        footer={null}
                        width={'fit-content'}
                        centered
                        destroyOnClose
                        bodyStyle={{ direction: 'rtl', textAlign: 'right', maxHeight: '90vh', overflowY: 'auto' }}
                    >
                        <div className={s.historyTableWrap}>
                            <Table
                                rowKey="id"
                                dataSource={recentMeetings}
                                columns={recentMeetingsColumns}
                                pagination={{ pageSize: 5, size: 'small', hideOnSinglePage: true }}
                                loading={recentMeetingsLoading}
                                size="middle"
                                locale={{ emptyText: 'אין פגישות מתאמות ב-90 הימים האחרונים' }}
                            />
                        </div>
                    </Modal>
                )}
                <Modal
                    title="ייצוא לאקסל"
                    visible={exportModalVisible}
                    onCancel={() => this.setState({ exportModalVisible: false })}
                    onOk={this.handleExport}
                    okText="ייצוא"
                    cancelText="ביטול"
                    confirmLoading={exportLoading}
                    width={340}
                >
                    <div className={s.exportModalBody} style={{ direction: 'rtl', textAlign: 'right' }}>
                        <div style={{ marginBottom: 8 }}>{'בחר חודש לייצוא'}</div>
                        <DatePicker.MonthPicker
                            value={exportMonth}
                            allowClear={false}
                            format="MM/YYYY"
                            disabledDate={isBeforeMeetingsStartMonth}
                            style={{ width: '100%' }}
                            onChange={date => date && this.setState({ exportMonth: date })}
                        />
                    </div>
                </Modal>
                <Modal
                    title="ייצוא לאקסל"
                    visible={exportRecentModalVisible}
                    onCancel={() => this.setState({ exportRecentModalVisible: false })}
                    onOk={this.handleExportRecent}
                    okText="ייצוא"
                    cancelText="ביטול"
                    confirmLoading={exportRecentLoading}
                    width={340}
                >
                    <div className={s.exportModalBody} style={{ direction: 'rtl', textAlign: 'right' }}>
                        <div style={{ marginBottom: 8 }}>{'בחר חודש לייצוא'}</div>
                        <DatePicker.MonthPicker
                            value={exportRecentMonth}
                            allowClear={false}
                            format="MM/YYYY"
                            disabledDate={isBeforeMeetingsStartMonth}
                            style={{ width: '100%' }}
                            onChange={date => date && this.setState({ exportRecentMonth: date })}
                        />
                    </div>
                </Modal>
            </Layout>
        )
    }
}

export default MentorsMeetings
